import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { axiosClient } from "@/lib/axiosClient";
import { Sun, Moon, Lock } from "lucide-react";

const ResetPasswordPage: React.FC = () => {
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");
    const [loading, setLoading] = useState(false);
    const [validLink, setValidLink] = useState<boolean | null>(null);
    const [darkMode, setDarkMode] = useState(false);

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const token = searchParams.get("token");
    const email = searchParams.get("email");

    // ✅ Cek token valid atau tidak saat pertama kali buka halaman
    useEffect(() => {
        const validateToken = async () => {
            if (!token || !email) {
                setValidLink(false);
                return;
            }
            try {
                await axiosClient.post("/validate-reset-token", { token, email });
                setValidLink(true);
            } catch {
                setValidLink(false);
            }
        };
        validateToken();
    }, [token, email]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!token || !email) {
            toast.error("Tautan tidak valid atau sudah kedaluwarsa.");
            return;
        }

        if (password !== passwordConfirmation) {
            toast.error("Kata sandi tidak cocok.");
            return;
        }

        setLoading(true);
        try {
            await axiosClient.post("/reset-password", {
                token,
                email,
                password,
                password_confirmation: passwordConfirmation,
            });

            toast.success("Kata sandi berhasil diubah.");
            setTimeout(() => navigate("/login"), 2500);
        } catch (error) {
            console.error(error);
            toast.error("Gagal mereset kata sandi. Tautan mungkin sudah kedaluwarsa.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className={`min-h-screen flex items-center justify-center relative transition-colors duration-500 ${darkMode
                    ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100"
                    : "bg-gradient-to-br from-[#eef6ff] via-[#e6efff] to-[#dce8ff] text-slate-800"
                }`}
        >
            {/* Toggle dark/light mode */}
            <button
                onClick={() => setDarkMode(!darkMode)}
                className="absolute top-5 right-5 p-2 rounded-full hover:bg-slate-200/30 dark:hover:bg-slate-700/50 transition"
                aria-label="Toggle mode"
            >
                {darkMode ? (
                    <Sun className="h-5 w-5 text-yellow-400" />
                ) : (
                    <Moon className="h-5 w-5 text-blue-700" />
                )}
            </button>

            {/* <Toaster position="bottom-center" /> */}

            <div
                className={`z-10 w-full max-w-md mx-4 rounded-2xl shadow-2xl p-8 border transition-all duration-500 backdrop-blur-md ${darkMode
                        ? "bg-slate-900/90 border-slate-700"
                        : "bg-white/80 border-white/30"
                    }`}
            >
                <div className="flex flex-col items-center mb-6">
                    <img
                        src="/konaspi.png"
                        alt="Logo Konaspi XII"
                        className="w-24 h-auto mb-3"
                    />
                    {/* Garis pemisah di bawah logo */}
                    <div className="w-40 h-[2px] bg-gradient-to-r from-indigo-400 via-purple-500 to-blue-500 rounded-full"></div>
                </div>
                <h2 className="text-2xl font-bold text-center mb-6">
                    Reset Kata Sandi
                </h2>

                {/* ✅ Jika link tidak valid */}
                {validLink === false && (
                    <div className="text-center text-red-600 font-medium">
                        Tautan ini tidak valid atau sudah kedaluwarsa.
                    </div>
                )}

                {/* ✅ Jika masih loading token atau link valid */}
                {validLink === null && (
                    <div className="text-center text-slate-500 animate-pulse">
                        Memverifikasi tautan...
                    </div>
                )}

                {/* ✅ Form tampil hanya jika link valid */}
                {validLink && (
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="relative">
                            <Lock className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Kata sandi baru"
                                required
                                className={`w-full rounded-lg border pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition ${darkMode
                                        ? "bg-slate-800 border-slate-700 text-slate-100 placeholder-slate-400"
                                        : "bg-white border-slate-300 text-slate-800 placeholder-slate-400"
                                    }`}
                            />
                        </div>

                        <div className="relative">
                            <Lock className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
                            <input
                                type="password"
                                value={passwordConfirmation}
                                onChange={(e) => setPasswordConfirmation(e.target.value)}
                                placeholder="Konfirmasi kata sandi"
                                required
                                className={`w-full rounded-lg border pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition ${darkMode
                                        ? "bg-slate-800 border-slate-700 text-slate-100 placeholder-slate-400"
                                        : "bg-white border-slate-300 text-slate-800 placeholder-slate-400"
                                    }`}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-lg shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? "Menyimpan..." : "Ubah Kata Sandi"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ResetPasswordPage;
